import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  BookOpen, 
  Clock, 
  Calendar, 
  CheckCircle, 
  AlertCircle,
  Play,
  Pause,
  Award,
  Building2,
  Users,
  MapPin,
  Star,
  Download,
  Eye,
  ArrowRight,
  Search,
  Filter,
  Sparkles,
  Target,
  Zap,
  Heart,
  FilterX
} from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import traineeService, { TraineeTraining } from '../../services/trainee.service';
import { favoritesService, FavoriteTraining } from '../../services/favorites.service';
import CountdownTimer from '../../components/common/CountdownTimer';
import { API_ENDPOINTS } from '../../config/api';

const MyTrainingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [trainings, setTrainings] = useState<TraineeTraining[]>([]);
  const [favorites, setFavorites] = useState<FavoriteTraining[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'enrolled' | 'favorites'>('enrolled');

  useEffect(() => {
    loadTrainings();
    loadFavorites();
  }, []);

  const loadTrainings = async () => {
    try {
      setLoading(true);
      const data = await traineeService.getMyTrainings();
      setTrainings(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const data = await favoritesService.getFavorites();
      setFavorites(data);
    } catch (error) {
    }
  };

  const filteredEnrolledTrainings = trainings.filter(training => {
    const matchesStatus = selectedStatus === 'all' || training.status === selectedStatus;
    const matchesSearch = training.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         training.center.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const filteredFavoriteTrainings = favorites.filter(favorite => {
    const matchesSearch = favorite.training.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (favorite.training as any).training_center?.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'enrolled':
      case 'in_progress':
        return 'bg-brand-primary/10 text-brand-primary border-brand-primary/20';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'enrolled':
        return t('trainee.myTrainings.status.enrolled');
      case 'in_progress':
        return t('trainee.myTrainings.status.in_progress');
      case 'completed':
        return t('trainee.myTrainings.status.completed');
      case 'cancelled':
        return t('trainee.myTrainings.status.cancelled');
      default:
        return t('trainee.myTrainings.status.unknown');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'enrolled':
        return <Play className="h-4 w-4" />;
      case 'in_progress':
        return <Clock className="h-4 w-4" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4" />;
      case 'cancelled':
        return <AlertCircle className="h-4 w-4" />;
      default:
        return <Pause className="h-4 w-4" />;
    }
  };

  const downloadCertificate = async (trainingId: number) => {
    try {
      const blob = await traineeService.downloadCertificate(trainingId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `certificate-${trainingId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <DashboardLayout title={t('trainee.myTrainings.title')}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t('trainee.myTrainings.title')}>
      <div className="space-y-4">
        {/* Welcome Section */}
        <div className="bg-gradient-to-br from-brand-primary/5 via-brand-accent/5 to-brand-surface rounded-xl p-4 border border-brand-primary/20 shadow-lg">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-3 mb-3">
                <div className="p-2 bg-gradient-to-br from-brand-primary to-brand-primary/80 rounded-lg shadow-lg self-start">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-brand-primary to-brand-accent bg-clip-text text-transparent">
                    {t('trainee.myTrainings.title')} 📚
                  </h2>
                  <p className="text-gray-600 text-sm">
                    {t('trainee.myTrainings.subtitle')}
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                <div className="flex items-center space-x-2 text-xs text-gray-600 bg-white/50 px-2 py-1 rounded">
                  <Target className="h-3 w-3" />
                  <span>{t('trainee.myTrainings.stats.enrolled', { count: trainings.length })}</span>
                </div>
                <div className="flex items-center space-x-2 text-xs text-gray-600 bg-white/50 px-2 py-1 rounded">
                  <CheckCircle className="h-3 w-3" />
                  <span>{t('trainee.myTrainings.stats.completed', { count: trainings.filter(t => t.status === 'completed').length })}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-lg">
          <div className="flex items-center gap-4 mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('trainee.myTrainings.title')}</h3>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setActiveTab('enrolled')}
                className={`px-6 py-2 text-sm font-medium transition-all duration-200 ${
                  activeTab === 'enrolled'
                    ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-brand-primary/5 hover:text-brand-primary'
                }`}
              >
                <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  <span>{t('trainee.myTrainings.tabs.enrolled')}</span>
                  <span className="bg-white/20 text-xs px-2 py-1 rounded-full">
                    {trainings.length}
                  </span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('favorites')}
                className={`px-6 py-2 text-sm font-medium transition-all duration-200 ${
                  activeTab === 'favorites'
                    ? 'bg-gradient-to-r from-brand-primary to-brand-secondary text-white shadow-lg'
                    : 'bg-white text-gray-600 hover:bg-brand-primary/5 hover:text-brand-primary'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Heart className="h-4 w-4" />
                  <span>{t('trainee.myTrainings.tabs.favorites')}</span>
                  <span className="bg-white/20 text-xs px-2 py-1 rounded-full">
                    {favorites.length}
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-lg">
          <div className="flex flex-col lg:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder={t('trainee.myTrainings.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent text-sm"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedStatus('all')}
                className={`px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  selectedStatus === 'all'
                    ? 'bg-brand-primary text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('trainee.myTrainings.filters.all', { count: trainings.length })}
              </button>
              <button
                onClick={() => setSelectedStatus('enrolled')}
                className={`px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  selectedStatus === 'enrolled'
                    ? 'bg-brand-primary text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('trainee.myTrainings.filters.enrolled', { count: trainings.filter(t => t.status === 'enrolled').length })}
              </button>
              <button
                onClick={() => setSelectedStatus('completed')}
                className={`px-3 py-2 rounded-lg font-medium transition-all text-sm ${
                  selectedStatus === 'completed'
                    ? 'bg-green-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('trainee.myTrainings.filters.completed', { count: trainings.filter(t => t.status === 'completed').length })}
              </button>
            </div>
          </div>
        </div>

        {/* Trainings Grid */}
        {(activeTab === 'enrolled' ? filteredEnrolledTrainings.length > 0 : filteredFavoriteTrainings.length > 0) ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeTab === 'enrolled' 
              ? filteredEnrolledTrainings.map((training, index) => (
              <motion.div
                key={training.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 hover:border-brand-primary/30"
              >
                {/* Image */}
                <div className="h-32 bg-gray-200 relative">
                  <img
                    src={training.image ? `${API_ENDPOINTS.UPLOADS}/${training.image}` : 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop'}
                    alt={training.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop';
                    }}
                    onLoad={() => {}}
                  />
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold border flex items-center gap-1 ${getStatusColor(training.status)}`}>
                      {getStatusIcon(training.status)}
                      {getStatusText(training.status)}
                    </span>
                  </div>
                  {/* {training.certificate && (
                    <div className="absolute top-2 right-2">
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-600 text-white flex items-center gap-1 shadow-lg">
                        <Award className="h-3 w-3" />
{t('trainee.myTrainings.status.certified')}
                      </span>
                    </div>
                  )} */}
                  <div className="absolute bottom-2 left-2">
                    <span className="px-2 py-1 bg-black/70 text-white text-xs font-medium rounded">
                      {formatCurrency(training.price)}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-1 mb-2">
                    <Building2 className="h-3 w-3 text-brand-primary" />
                    <span className="text-xs text-gray-600 font-medium truncate">{training.center.name}</span>
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-2 hover:text-brand-primary transition-colors line-clamp-2">
                    {training.title}
                  </h3>

                  <p className="text-gray-600 mb-3 text-sm line-clamp-2">
                    {training.description}
                  </p>

                  {/* Session Date with Countdown */}
                  {training.nextSession && (
                    <div className="mb-3 p-3 bg-gradient-to-r from-brand-primary/5 to-brand-accent/5 rounded-lg border border-brand-primary/20">
                      <div className="flex items-center gap-2 text-brand-primary mb-2">
                        <Calendar className="h-4 w-4" />
                        <span className="text-sm font-semibold">{t('trainee.myTrainings.sessionDate', { date: new Date(training.nextSession).toLocaleDateString('fr-FR') })}</span>
                      </div>
                      <CountdownTimer 
                        targetDate={training.nextSession} 
                        variant="compact"
                        className="text-xs"
                      />
                    </div>
                  )}

                  {/* Details */}
                  <div className="space-y-1 mb-3 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">{t('trainee.myTrainings.details.duration')}</span>
                      <span className="font-semibold text-gray-900">{training.duration}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">{t('trainee.myTrainings.details.level')}</span>
                      <span className="font-semibold text-gray-900">{training.level}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">{t('trainee.myTrainings.details.domain')}</span>
                      <span className="font-semibold text-gray-900 truncate">{training.domaine}</span>
                    </div>
                  </div>



                  {/* Actions */}
                  <div className="flex gap-2">
                    <button 
                      onClick={() => navigate(`/training-subscription/${training.id}`)}
                      className="flex-1 bg-gradient-to-r from-brand-primary to-brand-primary/80 text-white py-2 px-3 rounded-lg font-semibold hover:from-brand-primary/90 hover:to-brand-primary/70 transition-all text-sm"
                    >
                      <Eye className="h-4 w-4 mr-1 inline" />
                      {t('trainee.myTrainings.actions.view')}
                    </button>
                    {/* {training.certificate && (
                      <button 
                        onClick={() => downloadCertificate(training.id)}
                        className="px-3 py-2 border-2 border-green-600 text-green-600 rounded-lg font-semibold hover:bg-green-600 hover:text-white transition-all flex items-center gap-1 text-sm"
                      >
                        <Download className="h-4 w-4" />
                        {t('trainee.myTrainings.actions.certificate')}
                      </button>
                    )} */}
                  </div>
                </div>
              </motion.div>
            ))
              : filteredFavoriteTrainings.map((favorite, index) => (
              <motion.div
                key={favorite.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group hover:border-brand-primary/30"
              >
                {/* Image */}
                <div className="h-48 bg-gray-200 relative overflow-hidden">
                  <img
                    src={(favorite.training as any).banner_image ? `${API_ENDPOINTS.UPLOADS}/${(favorite.training as any).banner_image}` : 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop'}
                    alt={favorite.training.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.src = 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=400&h=250&fit=crop';
                    }}
                  />
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-500 text-white flex items-center gap-1 shadow-lg">
                      <Heart className="h-3 w-3 fill-current" />
{t('trainee.myTrainings.favorite')}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-center gap-1 mb-2">
                    <Building2 className="h-3 w-3 text-brand-primary" />
                    <span className="text-xs text-gray-600 font-medium truncate">{(favorite.training as any).training_center?.name || 'Centre'}</span>
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-2 hover:text-brand-primary transition-colors line-clamp-2">
                    {favorite.training.title}
                  </h3>

                  <p className="text-gray-600 mb-3 text-sm line-clamp-2">
                    {favorite.training.description}
                  </p>

                  {/* Details */}
                  <div className="space-y-1 mb-3 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">{t('trainee.myTrainings.details.format')}</span>
                      <span className="font-semibold text-gray-900">{favorite.training.format}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">{t('trainee.myTrainings.details.date')}</span>
                      <span className="font-semibold text-gray-900">{new Date(favorite.training.scheduleDate).toLocaleDateString('fr-FR')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">{t('trainee.myTrainings.details.duration')}</span>
                      <span className="font-semibold text-gray-900">{favorite.training.duration}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button 
                      onClick={() => navigate(`/training/${favorite.training.id}`)}
                      className="flex-1 bg-gradient-to-r from-brand-primary to-brand-primary/80 text-white py-2 px-3 rounded-lg font-semibold hover:from-brand-primary/90 hover:to-brand-primary/70 transition-all text-sm"
                    >
                      <Eye className="h-4 w-4 mr-1 inline" />
                      {t('trainee.myTrainings.actions.view')}
                    </button>
                    <button 
                      onClick={async () => {
                        await favoritesService.removeFromFavorites(favorite.training.id);
                        loadFavorites();
                      }}
                      className="px-3 py-2 border-2 border-red-500 text-red-500 rounded-lg font-semibold hover:bg-red-500 hover:text-white transition-all flex items-center gap-1 text-sm"
                    >
                      <Heart className="h-4 w-4 fill-current" />
                      {t('trainee.myTrainings.actions.remove')}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          /* No results */
          <div className="text-center py-8 bg-white rounded-xl border border-gray-200 shadow-lg">
            <div className="p-4 bg-gradient-to-br from-brand-primary/10 to-brand-accent/10 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <BookOpen className="h-8 w-8 text-brand-primary" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {t('trainee.myTrainings.noResults.title')}
            </h3>
            <p className="text-gray-600 mb-6 max-w-md mx-auto text-sm">
              {searchTerm || selectedStatus !== 'all' 
                ? t('trainee.myTrainings.noResults.description')
                : t('trainee.myTrainings.noResults.noEnrolledTrainings')
              }
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedStatus('all');
              }}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-brand-primary to-brand-primary/80 text-white font-semibold rounded-lg hover:from-brand-primary/90 hover:to-brand-primary/70 transition-all shadow-lg text-sm"
            >
              <Search className="h-4 w-4 mr-2" />
              {t('trainee.myTrainings.noResults.viewAllTrainings')}
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MyTrainingsPage;
